import { ReactNode } from "react";
import { Link } from "react-router-dom";
import TriEventsLoadingScreen from "../common/TriEventsLoadingScreen";
import { Plus } from "lucide-react";

// Services
import { useOrganization } from "@/services/organization/getOrganizationDetails"

// Hooks
import { useTenantNavigation } from '../../hooks/navigation/useTenantNavigation';

// shadcn components
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

interface OrganizationLayoutProps {
    children: ReactNode;
    showCreateEvent?: boolean;
}

function OrganizationLayout({ children, showCreateEvent = false }: OrganizationLayoutProps) {
    const { organization, loading, error } = useOrganization();
    const { getTenantPath } = useTenantNavigation();

    if (loading) {
        return <TriEventsLoadingScreen />
    }

    if (error) {
        return (
            <Card className="border-2 h-full flex items-center justify-center">
                <CardContent className="text-center space-y-4 py-8">
                    <h2 className="text-2xl font-bold">Organization Not Found</h2>
                    <p className="text-muted-foreground">
                        {error instanceof Error ? error.message : String(error)}
                    </p>
                    <Button asChild variant="default" size="lg">
                        <Link to="/">Return Home</Link>
                    </Button>
                </CardContent>
            </Card>
        );
    }

    if (!organization) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Loading organization...</p>
                </div>
            </div>
        );
    }

    return (
        <div>
            {showCreateEvent && (
                <div className="space-y-4">
                    <div className="flex flex-col md:flex-row gap-4">
                        <Card className="flex-1">
                            <CardContent className="p-8">
                                <div className="space-y-4">
                                    <div>
                                        <h1 className="text-4xl font-bold tracking-tight">
                                            {organization.name}
                                        </h1>
                                        <Separator className="mt-3 h-[2px] bg-black/10" />
                                    </div>
                                    <div className="max-h-12 overflow-y-auto">
                                        <p className="text-muted-foreground text-lg leading-relaxed">
                                            {organization.description}
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="w-full md:w-[300px] h-fit">
                            <CardContent className="p-6">
                                <div className="space-y-4">
                                    <div>
                                        <h3 className="text-lg font-semibold">Ready to create something amazing?</h3>
                                        <p className="text-muted-foreground mt-1">Start planning your next event</p>
                                    </div>
                                    <Button
                                        asChild
                                        size="lg"
                                        className="w-full gap-3 text-lg transition-transform duration-200 hover:scale-105"
                                    >
                                        <Link to={getTenantPath('/events/create')}>
                                            <Plus className="h-6 w-6 stroke-[3.5]" />
                                            Create Event
                                        </Link>
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {children}
                </div>
            )}
            {!showCreateEvent && (
                <div>
                    {children}
                </div>
            )}
        </div>
    );
}

export default OrganizationLayout; 